/**
 * A notice in the corner: something happened, or needs a glance.
 *
 * Four tones, each a round mark in its colour beside the title: `info`
 * (purple), `success` (jade), `warning` (marigold) and `danger` (coral). The
 * mark's glyph changes with the tone too — info's letter, a check, the
 * warning triangle, an exclamation — so a tone is never told by colour alone.
 * A detail line can follow, and `busy` sets a spinner in front of it —
 * "Reconnecting…". With `onDismiss` it has a close button, and then a
 * `dismissLabel` to name it.
 *
 * ToastStack is where they go: the top right of the screen, newest on top,
 * above the page — so a toast stays live over a dialog — and clear of what the
 * shell keeps at the edges. Each toast is said once: inside a stack it adds
 * its title and detail as a line to one of the two live regions the stack
 * keeps beside the cards — a warning or danger's said at once, the rest when
 * the reader is free — and takes the line away as it goes. The cards are no
 * region themselves: an alert card in a polite stack was said twice, once by
 * each. Keep the stack mounted while it is empty, so its regions are there
 * before the first toast.
 *
 *   <ToastStack>
 *     <Toast tone="warning" title="Microphone unavailable"
 *            detail="The microphone could not be started. Your teammates will not hear you."
 *            onDismiss={dismiss} dismissLabel={t('common.close')} />
 *   </ToastStack>
 */

import { createContext, useContext, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { createPortal } from 'react-dom';

import { abovePage } from './page-layers';
import { CloseButton } from './close-button';
import { GLYPH } from './glyphs';
import { Spinner } from './spinner';
import { ToneMark } from './tone-mark';
import utilities from '../../styles/utilities.module.css';
import styles from './toast.module.css';

/** Each tone's mark — the round mark in its tone (ToneMark) — glyph, and whether it is said at once. */
const TONE = {
  info: { mark: 'accent', glyph: GLYPH.infoLetter, urgent: false },
  success: { mark: 'success', glyph: GLYPH.check, urgent: false },
  warning: { mark: 'warning', glyph: GLYPH.warning, urgent: true },
  danger: { mark: 'danger', glyph: GLYPH.exclamation, urgent: true },
} as const;

export type ToastProps = {
  tone?: keyof typeof TONE;
  title: ReactNode;
  detail?: ReactNode;
  /** A spinner before the detail: the thing is still happening. */
  busy?: boolean;
} & (
  | {
      onDismiss: () => void;
      /** The close button's accessible name: a dismissable toast must have one. */
      dismissLabel: string;
    }
  | { onDismiss?: never; dismissLabel?: never }
);

/** The stack's two live regions, once they are on the page: what a toast inside it is said in. */
const StackRegions = createContext<{ polite: HTMLElement | null; urgent: HTMLElement | null } | null>(null);

export function Toast(props: ToastProps) {
  const { tone = 'info', title, detail, busy = false } = props;
  const { mark, glyph, urgent } = TONE[tone];
  const regions = useContext(StackRegions);
  const region = urgent ? regions?.urgent : regions?.polite;
  return (
    <div className={styles.toast}>
      {/* Its line in the stack's region: added as it arrives, so it alone is said. */}
      {region &&
        // design-checks:allow-portal — no layer: a line in the live region its stack keeps.
        createPortal(
          <p>
            {title}
            {detail != null && <> {detail}</>}
          </p>,
          region,
        )}
      <ToneMark tone={mark} glyph={glyph} />
      <div className={styles.text}>
        <p className={styles.title}>{title}</p>
        {(detail || busy) && (
          <p className={styles.detail}>
            {busy && <Spinner size="sm" tone="soft" />}
            {detail}
          </p>
        )}
      </div>
      {props.onDismiss && <CloseButton label={props.dismissLabel} onClick={props.onDismiss} />}
    </div>
  );
}

/**
 * The corner the toasts stack in, rendered above the page: give it its
 * toasts newest first, and render it while it is empty too. Beside the cards,
 * the two regions the toasts in it are said in, empty of anything of their
 * own: each toast puts its line there. It goes above the page on the toasts'
 * rung over the dialogs, named as it goes (abovePage), so what hangs from a
 * control on a toast hangs over it, live, not under it with the page
 * (useHang).
 */
export function ToastStack({ children }: { children?: ReactNode }) {
  const [polite, setPolite] = useState<HTMLElement | null>(null);
  const [urgent, setUrgent] = useState<HTMLElement | null>(null);
  const regions = useMemo(() => ({ polite, urgent }), [polite, urgent]);
  return abovePage(
    <StackRegions.Provider value={regions}>
      <div className={styles.stack}>{children}</div>
      {/* design-checks:allow-hidden-region — the regions the toasts' lines are rendered into, not a line. */}
      <div ref={setPolite} className={utilities.visuallyHidden} aria-live="polite" />
      <div ref={setUrgent} className={utilities.visuallyHidden} aria-live="assertive" />
    </StackRegions.Provider>,
    'toast',
  );
}
